import { useEffect } from 'react';

import { Logger } from 'lib/logger';

type PerformanceObject = {
  ttfb: number;
  lcpValue: number;
  lcpSubparts?: {
    ttfb: number;
    resourceLoadDuration?: number;
    resourceLoadDelay?: number;
    elementRenderDelay?: number;
  };
  fcp: number;
  cls: number;
};

const initialPerformanceObject: PerformanceObject = {
  lcpValue: 0,
  lcpSubparts: {
    ttfb: 0,
  },
  ttfb: 0,
  fcp: 0,
  cls: 0,
};
let performanceObject: PerformanceObject = { ...initialPerformanceObject };

const updateTtfb = () => {
  performance.getEntriesByType('navigation').forEach((nav) => {
    // @ts-ignore
    performanceObject.ttfb = nav.responseStart;

    if (performanceObject.lcpSubparts) {
      performanceObject.lcpSubparts.ttfb = performanceObject.ttfb;
    }
  });
};

const observeFcp = () => {
  new PerformanceObserver((entryList) => {
    entryList.getEntries().forEach((entry: any) => {
      if (entry.name === 'first-contentful-paint') {
        performanceObject.fcp = entry.renderTime || entry.startTime;
      }
    });
  }).observe({ type: 'paint', buffered: true });
};

const observeLcp = () => {
  new PerformanceObserver((entryList) => {
    entryList.getEntries().forEach((entry: any) => {
      performanceObject.lcpValue = entry.renderTime || entry.startTime;

      if (!entry?.element && entry?.element?.tagName !== 'IMG' && !entry?.url) {
        return;
      }

      const lcpImageUrl = entry?.element?.currentSrc || entry?.element?.src || entry?.url;
      const entries = performance.getEntriesByType('resource');

      if (!lcpImageUrl) {
        return;
      }

      const image: any | undefined = entries.find((e: any) => e.name === lcpImageUrl);

      if (!image) {
        return;
      }

      if (!performanceObject.lcpSubparts) {
        performanceObject.lcpSubparts = {
          ttfb: performanceObject.ttfb,
        };
      }

      performanceObject.lcpSubparts.resourceLoadDuration = image.duration;
      performanceObject.lcpSubparts.resourceLoadDelay = image.fetchStart - performanceObject.ttfb;
      // eslint-disable-next-line max-len
      performanceObject.lcpSubparts.elementRenderDelay = performanceObject.lcpValue - image.responseEnd;
    });
  })
    .observe({ type: 'largest-contentful-paint', buffered: true });
};

const observeCls = () => {
  new PerformanceObserver((entryList) => {
    entryList.getEntries().forEach((entry: any) => {
      if (!entry.hadRecentInput) {
        performanceObject.cls += entry.value || 0;
      }
    });
  })
    .observe({ type: 'layout-shift', buffered: true });
};

export const useWebVitalsRecording = () => {
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_DD_WEB_VITALS_LOGGING !== 'true') {
      return;
    }

    updateTtfb();

    observeFcp();

    observeLcp();

    observeCls();

    const executeOnIdle = window.requestIdleCallback || setTimeout;

    executeOnIdle(() => {
      if (!performanceObject.lcpValue) {
        setTimeout(() => {
          executeOnIdle(() => {
            Logger.log({ webVitals: performanceObject, message: 'web-vitals-track' });
          });
        }, 3_000);

        return;
      }

      Logger.log({ webVitals: performanceObject, message: 'web-vitals-track' });
    });

    return () => {
      performanceObject = { ...initialPerformanceObject };
    };
  }, []);
};
